/**
 * Contrats d'appel de l'API SYGAP.
 *
 * Les vues ne connaissent que ces fonctions : aucune n'assemble une URL ni ne
 * manipule un jeton. Les types décrivent ce que l'API rend **réellement**, sans
 * enjoliver — ce qui n'est pas renvoyé n'est pas déclaré.
 */

import type {
  CategorieEmbarcation,
  CategorieProfessionnel,
  CodeTypePersonne,
  StatutDemande,
  StatutEmbarcation,
  StatutInstruction,
  StatutProfessionnel,
  TypePieceIdentite,
} from '@sygap/shared';
import { appelerApi, telechargerApi, type FichierApi } from './client';

// -----------------------------------------------------------------------------
// Authentification
// -----------------------------------------------------------------------------

export interface ReponseConnexion {
  readonly accessToken: string;
  readonly forceChangementMdp: boolean;
  readonly expiresIn: number;
}

export async function seConnecter(login: string, motDePasse: string): Promise<ReponseConnexion> {
  return appelerApi<ReponseConnexion>('/auth/connexion', {
    methode: 'POST',
    corps: { login, motDePasse },
  });
}

/**
 * Ferme la session côté serveur (US-007).
 *
 * Effacer le jeton du navigateur ne prouve rien : il suffit de l'avoir recopié.
 * C'est le serveur qui doit cesser de l'accepter.
 */
export async function seDeconnecter(jeton: string): Promise<void> {
  await appelerApi('/auth/deconnexion', { methode: 'POST', jeton });
}

/** Changement de mot de passe par l'agent lui-même (US-009). */
export async function changerMotDePasse(
  jeton: string,
  motDePasseActuel: string,
  nouveauMotDePasse: string,
): Promise<{ readonly change: true; readonly sessionsFermees: number }> {
  return appelerApi('/auth/mot-de-passe', {
    methode: 'POST',
    jeton,
    corps: { motDePasseActuel, nouveauMotDePasse },
  });
}

// -----------------------------------------------------------------------------
// Dossiers
// -----------------------------------------------------------------------------

export interface Page<T> {
  readonly donnees: readonly T[];
  readonly total: number;
  readonly page: number;
  readonly taille: number;
}

export interface DemandeurResume {
  readonly idProfessionnel: number;
  readonly categorie: string;
  readonly nom: string | null;
  readonly prenom: string | null;
  readonly raisonSociale: string | null;
}

export interface DemandeListe {
  readonly idDemande: number;
  readonly referenceDemande: string;
  readonly dateSoumission: string;
  readonly natureDemande: string;
  readonly statut: StatutDemande;
  readonly etapeCourante: string | null;
  readonly demandeur: DemandeurResume;
  readonly typeAutorisation: { readonly code: string; readonly libelle: string };
}

export interface EtapeCircuit {
  readonly idAdministration: number;
  readonly sigle: string;
  readonly ordreDansCircuit: number;
  readonly delaiSlaJours: number;
}

export interface RetardEtape {
  readonly idAdministration: number;
  readonly sigle: string;
  readonly echeance: string;
  readonly joursRetard: number;
  readonly enCours: boolean;
}

/** Photographie rendue par le moteur de SLA. Jamais stockée, toujours recalculée. */
export interface EtatDossier {
  readonly etapeCourante: EtapeCircuit | null;
  readonly etapesValidees: number;
  readonly etapesTotal: number;
  readonly progression: number;
  readonly complet: boolean;
  readonly rejete: boolean;
  readonly retards: readonly RetardEtape[];
  readonly enRetard: boolean;
  readonly joursRetardMax: number;
  readonly peutPasserEnSignature: boolean;
}

export interface InstructionDossier {
  readonly idInstruction: number;
  readonly idAdministration: number;
  readonly dateReception: string;
  readonly dateTraitement: string | null;
  readonly statutInstruction: StatutInstruction;
  readonly observations: string | null;
  readonly administration: {
    readonly idAdministration: number;
    readonly sigle: string;
    readonly ordreDansCircuit: number;
  };
}

export interface MouvementWorkflow {
  readonly etapePrecedente: string;
  readonly etapeCourante: string;
  readonly actionEffectuee: string;
  readonly horodatage: string;
  readonly dureeDepuisPrecMin: number | null;
  readonly administration: { readonly sigle: string } | null;
}

export interface DemandeFiche extends DemandeListe {
  readonly observations: string | null;
  /**
   * Le type d'autorisation est plus détaillé sur la fiche que sur la liste :
   * `paiementRequis` y dit s'il y a une redevance à émettre, et il vient du
   * référentiel — jamais d'une liste de codes en dur côté client, qui
   * divergerait au premier type ajouté par l'administration système.
   */
  readonly typeAutorisation: {
    readonly code: string;
    readonly libelle: string;
    readonly paiementRequis: boolean;
  };
  readonly demandeEmbarcations: readonly {
    readonly embarcation: {
      readonly idEmbarcation: number;
      readonly categorie: string;
      readonly numImmatriculation: string;
      readonly nomEmbarcation: string | null;
    };
  }[];
  readonly instructions: readonly InstructionDossier[];
  readonly historique: readonly MouvementWorkflow[];
  readonly etat: EtatDossier;
}

export interface FiltresDemandes {
  readonly recherche?: string;
  readonly statut?: StatutDemande;
  readonly page?: number;
  readonly taille?: number;
}

export async function listerDemandes(
  jeton: string,
  filtres: FiltresDemandes = {},
  signal?: AbortSignal,
): Promise<Page<DemandeListe>> {
  const parametres = new URLSearchParams();
  if (filtres.recherche !== undefined && filtres.recherche !== '') {
    parametres.set('recherche', filtres.recherche);
  }
  if (filtres.statut !== undefined) parametres.set('statut', filtres.statut);
  if (filtres.page !== undefined) parametres.set('page', String(filtres.page));
  if (filtres.taille !== undefined) parametres.set('taille', String(filtres.taille));

  const requete = parametres.toString();
  return appelerApi<Page<DemandeListe>>(`/demandes${requete === '' ? '' : `?${requete}`}`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export async function obtenirDemande(
  jeton: string,
  idDemande: number,
  signal?: AbortSignal,
): Promise<DemandeFiche> {
  return appelerApi<DemandeFiche>(`/demandes/${String(idDemande)}`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export async function transmettreDossier(jeton: string, idDemande: number): Promise<unknown> {
  return appelerApi(`/demandes/${String(idDemande)}/transmission`, {
    methode: 'POST',
    jeton,
  });
}

export async function rendreAvis(
  jeton: string,
  idDemande: number,
  statutInstruction: Extract<StatutInstruction, 'valide' | 'rejete'>,
  observations?: string,
): Promise<unknown> {
  return appelerApi(`/demandes/${String(idDemande)}/avis`, {
    methode: 'POST',
    jeton,
    corps: {
      statutInstruction,
      ...(observations === undefined || observations === '' ? {} : { observations }),
    },
  });
}

// -----------------------------------------------------------------------------
// Titres
// -----------------------------------------------------------------------------

export interface TitreListe {
  /** Identifiant normalisé du titre, quelle que soit sa nature. */
  readonly id: number;
  readonly type: 'licence' | 'permis';
  readonly numeroOfficiel: string | null;
  readonly statut: string;
  readonly statutApi: string;
  readonly dateEmission: string;
  readonly dateExpiration: string;
  readonly zoneAutorisee: string;
  readonly embarcation: {
    readonly idEmbarcation: number;
    readonly numImmatriculation: string;
    readonly nomEmbarcation: string | null;
  };
  readonly demande: { readonly idDemande: number; readonly referenceDemande: string };
}

export async function listerTitres(jeton: string, signal?: AbortSignal): Promise<Page<TitreListe>> {
  return appelerApi<Page<TitreListe>>('/titres', {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Récupère le PDF d'un titre.
 *
 * Le serveur compte le tirage et le journalise : cet appel n'est pas anodin, il
 * ne doit donc pas partir au chargement d'une page, seulement sur un geste
 * explicite de l'usager.
 */
export async function telechargerPdfTitre(
  jeton: string,
  type: 'licence' | 'permis',
  id: number,
): Promise<FichierApi> {
  return telechargerApi(`/titres/${type}/${String(id)}/pdf`, { jeton });
}

/** Les trois états qu'un agent peut poser sur un titre (US-025). */
export type StatutTitreApi = 'actif' | 'suspendu' | 'annule';

/**
 * Suspend, annule ou rétablit un titre (US-025).
 *
 * Le **motif** est facultatif dans le contrat de l'API mais exigé par
 * l'interface : une suspension sans motif ne se défend pas devant le titulaire,
 * et c'est le journal d'audit qui la conserve. Le serveur refuse par ailleurs
 * toute sortie de l'annulation — elle est définitive — et l'interface ne propose
 * donc plus rien sur un titre annulé.
 */
export async function changerStatutTitre(
  jeton: string,
  type: 'licence' | 'permis',
  id: number,
  statut: StatutTitreApi,
  motif: string,
): Promise<{ readonly statut: string; readonly statutApi: string }> {
  return appelerApi(`/titres/${type}/${String(id)}/statut`, {
    methode: 'POST',
    jeton,
    corps: { statut, motif: motif.trim() },
  });
}

// -----------------------------------------------------------------------------
// Délivrance
// -----------------------------------------------------------------------------

export interface BlocageDelivrance {
  readonly code: string;
  readonly message: string;
  readonly detail: Readonly<Record<string, unknown>>;
}

export type DecisionDelivrance =
  | {
      readonly delivrable: true;
      readonly documentProduit: string;
      readonly exemptDePaiement: boolean;
    }
  | { readonly delivrable: false; readonly blocages: readonly BlocageDelivrance[] };

export async function evaluerDelivrance(
  jeton: string,
  idDemande: number,
  signal?: AbortSignal,
): Promise<DecisionDelivrance> {
  return appelerApi<DecisionDelivrance>(`/titres/eligibilite/${String(idDemande)}`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export interface ResultatSignature {
  readonly demande: { readonly statut: string };
  readonly exemptDePaiement: boolean;
  readonly titres: readonly {
    readonly type: string;
    readonly id: number;
    readonly numeroOfficiel: string;
    readonly uuidDocument: string;
    readonly immatriculation: string;
  }[];
}

export async function signerDossier(
  jeton: string,
  idDemande: number,
  zoneAutorisee: string,
  typePeche?: string,
): Promise<ResultatSignature> {
  return appelerApi<ResultatSignature>(`/titres/signature/${String(idDemande)}`, {
    methode: 'POST',
    jeton,
    corps: {
      zoneAutorisee,
      ...(typePeche === undefined || typePeche === '' ? {} : { typePeche }),
    },
  });
}

// -----------------------------------------------------------------------------
// Référentiels et flotte (dépôt d'une demande)
// -----------------------------------------------------------------------------

export interface TypeAutorisation {
  readonly idTypeAutorisation: number;
  readonly code: string;
  readonly libelle: string;
  readonly documentProduit: string;
  readonly embarcationRequise: boolean;
  readonly dureeValiditeMois: number | null;
}

export async function listerTypesAutorisation(
  jeton: string,
  signal?: AbortSignal,
): Promise<readonly TypeAutorisation[]> {
  return appelerApi<readonly TypeAutorisation[]>('/demandes/types-autorisation', {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export interface EmbarcationListe {
  readonly idEmbarcation: number;
  readonly categorie: string;
  readonly numImmatriculation: string;
  readonly nomEmbarcation: string | null;
  readonly statut: string;
}

/**
 * Flotte de l'appelant. Le serveur cloisonne : un professionnel ne reçoit que
 * les embarcations qu'il possède ou qu'il arme, quel que soit le filtre demandé.
 */
export async function listerMaFlotte(
  jeton: string,
  signal?: AbortSignal,
): Promise<Page<EmbarcationListe>> {
  return appelerApi<Page<EmbarcationListe>>('/embarcations?taille=100', {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Saisie d'une embarcation, telle que la porte le formulaire.
 *
 * Les champs propres au navire sont optionnels **ici** parce qu'une pirogue n'en
 * a aucun ; ils redeviennent obligatoires côté serveur dès que la catégorie vaut
 * `navire`, et la base le garantit par un CHECK. L'interface ne relâche donc
 * rien : elle décrit un formulaire à deux visages, pas une règle affaiblie.
 */
export interface SaisieEmbarcation {
  readonly categorie: CategorieEmbarcation;
  readonly idProprietaire: number;
  readonly idArmateur?: number | undefined;
  readonly numImmatriculation: string;
  readonly nomEmbarcation?: string | undefined;
  readonly longueurHorsTout?: number | undefined;
  readonly materiau?: string | undefined;
  readonly motorisee?: boolean | undefined;
  readonly zonePecheHabituelle?: string | undefined;
  // --- Navire seulement ---
  readonly pavillon?: string | undefined;
  readonly dateFabrication?: string | undefined;
  readonly lieuFabrication?: string | undefined;
  readonly dateNationalite?: string | undefined;
  readonly typeNavire?: string | undefined;
  readonly typeAcquisition?: string | undefined;
  readonly jaugeBrute?: number | undefined;
}

/** Retire les champs vides : l'API refuse une chaîne vide là où elle attend une absence. */
function sansVides(entrees: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(entrees).filter(
      ([, valeur]) => valeur !== undefined && valeur !== null && valeur !== '',
    ),
  );
}

/**
 * Enregistre une embarcation au registre national (US-014, US-015).
 *
 * **Les champs propres au navire ne sont envoyés que pour un navire.** Ce n'est
 * pas une politesse : `chk_emb_pirogue` (migration 003) interdit à une pirogue de
 * porter jauge, pavillon, fabrication, acte de nationalité, type de navire ou
 * type d'acquisition. Les transmettre à vide ferait échouer l'écriture au niveau
 * base, avec une erreur que l'agent ne saurait pas lire.
 *
 * `dateExpNationalite` n'est jamais transmise : la colonne est calculée par la
 * base (`date_nationalite + 5 ans`) et l'écrire ferait échouer la requête.
 */
export async function creerEmbarcation(
  jeton: string,
  saisie: SaisieEmbarcation,
): Promise<EmbarcationListe> {
  const commun = sansVides({
    categorie: saisie.categorie,
    idProprietaire: saisie.idProprietaire,
    idArmateur: saisie.idArmateur,
    numImmatriculation: saisie.numImmatriculation.trim(),
    nomEmbarcation: saisie.nomEmbarcation?.trim(),
    longueurHorsTout: saisie.longueurHorsTout,
    materiau: saisie.materiau?.trim(),
    motorisee: saisie.motorisee,
    zonePecheHabituelle: saisie.zonePecheHabituelle?.trim(),
  });

  const propreAuNavire =
    saisie.categorie === 'navire'
      ? sansVides({
          pavillon: saisie.pavillon?.trim(),
          dateFabrication: saisie.dateFabrication,
          lieuFabrication: saisie.lieuFabrication?.trim(),
          dateNationalite: saisie.dateNationalite,
          typeNavire: saisie.typeNavire?.trim(),
          typeAcquisition: saisie.typeAcquisition?.trim(),
          jaugeBrute: saisie.jaugeBrute,
        })
      : {};

  return appelerApi<EmbarcationListe>('/embarcations', {
    methode: 'POST',
    jeton,
    corps: { ...commun, ...propreAuNavire },
  });
}

/**
 * Modifie une embarcation.
 *
 * `categorie`, `dateFabrication` et `lieuFabrication` sont absents du contrat :
 * une pirogue ne devient pas un navire, et la fabrication est immuable, garantie
 * par un trigger PostgreSQL. Les proposer à la modification donnerait un
 * formulaire qui échoue à l'enregistrement pour une raison invisible à l'agent.
 */
export async function modifierEmbarcation(
  jeton: string,
  idEmbarcation: number,
  modifications: Partial<
    Pick<
      SaisieEmbarcation,
      | 'nomEmbarcation'
      | 'idArmateur'
      | 'longueurHorsTout'
      | 'materiau'
      | 'motorisee'
      | 'zonePecheHabituelle'
      | 'pavillon'
      | 'dateNationalite'
      | 'typeNavire'
      | 'jaugeBrute'
    >
  > & { readonly statut?: StatutEmbarcation },
): Promise<EmbarcationListe> {
  return appelerApi<EmbarcationListe>(`/embarcations/${String(idEmbarcation)}`, {
    methode: 'PATCH',
    jeton,
    corps: sansVides({ ...modifications }),
  });
}

export async function deposerDemande(
  jeton: string,
  demande: {
    idDemandeur: number;
    idTypeAutorisation: number;
    natureDemande: string;
    idEmbarcations: readonly number[];
    observations?: string;
  },
): Promise<{ readonly idDemande: number; readonly referenceDemande: string }> {
  return appelerApi('/demandes', {
    methode: 'POST',
    jeton,
    corps: {
      idDemandeur: demande.idDemandeur,
      idTypeAutorisation: demande.idTypeAutorisation,
      natureDemande: demande.natureDemande,
      idEmbarcations: demande.idEmbarcations,
      ...(demande.observations === undefined || demande.observations === ''
        ? {}
        : { observations: demande.observations }),
    },
  });
}

// -----------------------------------------------------------------------------
// Référentiel des professionnels (US-011 à US-013)
// -----------------------------------------------------------------------------

/**
 * Ligne du référentiel des acteurs.
 *
 * `nom`/`prenom` et `raisonSociale` sont **exclusifs** : la base l'impose par le
 * CHECK ★N01 selon la catégorie. Les deux sont donc déclarés nullables ici, et
 * c'est `nommerProfessionnel` qui tranche à l'affichage — pas chaque écran.
 */
export interface ProfessionnelListe {
  readonly idProfessionnel: number;
  readonly categorie: string;
  readonly nom: string | null;
  readonly prenom: string | null;
  readonly raisonSociale: string | null;
  readonly numRegistreCommerce: string | null;
  readonly nationalite: string;
  readonly telephone: string | null;
  readonly email: string | null;
  readonly adresse: string | null;
  readonly numPieceIdentite: string | null;
  readonly typePieceIdentite: string | null;
  readonly dateNaissance: string | null;
  readonly lieuNaissance: string | null;
  readonly statut: string;
  readonly dateEnregistrement: string;
}

/** Rôle exercé, daté : `dateFin` non nulle signale un rôle échu (★N02). */
export interface RoleProfessionnel {
  readonly idTypePersonne: number;
  readonly dateDebut: string;
  readonly dateFin: string | null;
  readonly typePersonne: { readonly code: string; readonly libelle: string };
}

export interface TitreDeDossier {
  readonly numeroOfficiel: string | null;
  readonly statut: string;
  readonly dateEmission: string;
  readonly dateExpiration: string;
}

export interface DemandeDeProfessionnel {
  readonly idDemande: number;
  readonly referenceDemande: string;
  readonly statut: string;
  readonly natureDemande: string;
  readonly dateSoumission: string;
  readonly typeAutorisation: { readonly code: string; readonly libelle: string };
  readonly licences: readonly ({ readonly idLicence: number } & TitreDeDossier)[];
  readonly permis: readonly ({ readonly idPermis: number } & TitreDeDossier)[];
}

/**
 * Fiche complète (US-012). Propriété et armement sont **deux listes distinctes** :
 * en affrètement, le propriétaire et l'armateur ne sont pas le même acteur, et
 * les fondre en une « flotte » effacerait la distinction que la fiche sert à lire.
 */
export interface ProfessionnelFiche extends ProfessionnelListe {
  readonly professionnelTypes: readonly RoleProfessionnel[];
  readonly embarcationsParProprietaire: readonly EmbarcationListe[];
  readonly embarcationsParArmateur: readonly EmbarcationListe[];
  readonly demandes: readonly DemandeDeProfessionnel[];
}

/**
 * Saisie d'un professionnel, telle que la porte le formulaire.
 *
 * Comme pour l'embarcation, les champs des deux catégories cohabitent dans le
 * type et c'est l'envoi qui trie : le CHECK ★N01 exige nom **et** prénom d'une
 * personne physique, raison sociale **et** registre de commerce d'une personne
 * morale, et refuse le mélange.
 */
export interface SaisieProfessionnel {
  readonly categorie: CategorieProfessionnel;
  readonly nationalite: string;
  readonly nom?: string | undefined;
  readonly prenom?: string | undefined;
  readonly dateNaissance?: string | undefined;
  readonly lieuNaissance?: string | undefined;
  readonly raisonSociale?: string | undefined;
  readonly numRegistreCommerce?: string | undefined;
  readonly telephone?: string | undefined;
  readonly email?: string | undefined;
  readonly adresse?: string | undefined;
  readonly numPieceIdentite?: string | undefined;
  readonly typePieceIdentite?: TypePieceIdentite | undefined;
  readonly roles?: readonly CodeTypePersonne[] | undefined;
}

export interface FiltresProfessionnels {
  readonly recherche?: string;
  readonly categorie?: CategorieProfessionnel | '';
  readonly page?: number;
}

export async function listerProfessionnels(
  jeton: string,
  filtres: FiltresProfessionnels = {},
  signal?: AbortSignal,
): Promise<Page<ProfessionnelListe>> {
  const parametres = new URLSearchParams();
  if (filtres.recherche !== undefined && filtres.recherche.trim() !== '') {
    parametres.set('recherche', filtres.recherche.trim());
  }
  if (filtres.categorie !== undefined && filtres.categorie !== '') {
    parametres.set('categorie', filtres.categorie);
  }
  parametres.set('page', String(filtres.page ?? 1));
  return appelerApi<Page<ProfessionnelListe>>(`/professionnels?${parametres.toString()}`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export async function obtenirProfessionnel(
  jeton: string,
  idProfessionnel: number,
  signal?: AbortSignal,
): Promise<ProfessionnelFiche> {
  return appelerApi<ProfessionnelFiche>(`/professionnels/${String(idProfessionnel)}`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Enregistre un professionnel (US-011).
 *
 * **Les champs de l'autre catégorie ne sont jamais transmis.** Envoyer une
 * raison sociale vide sur une personne physique ferait échouer le CHECK ★N01 au
 * niveau base, avec une erreur qu'un agent de guichet ne saurait pas lire.
 *
 * Les rôles partent en tableau et non un par un : le service les affecte dans la
 * **même transaction** que la création, et un rôle inconnu annule toute la fiche
 * plutôt que d'en laisser une à moitié constituée.
 */
export async function creerProfessionnel(
  jeton: string,
  saisie: SaisieProfessionnel,
): Promise<ProfessionnelListe> {
  const commun = sansVides({
    categorie: saisie.categorie,
    nationalite: saisie.nationalite.trim(),
    telephone: saisie.telephone?.trim(),
    email: saisie.email?.trim(),
    adresse: saisie.adresse?.trim(),
    numPieceIdentite: saisie.numPieceIdentite?.trim(),
    typePieceIdentite: saisie.typePieceIdentite,
  });

  const propreALaCategorie =
    saisie.categorie === 'physique'
      ? sansVides({
          nom: saisie.nom?.trim(),
          prenom: saisie.prenom?.trim(),
          dateNaissance: saisie.dateNaissance,
          lieuNaissance: saisie.lieuNaissance?.trim(),
        })
      : sansVides({
          raisonSociale: saisie.raisonSociale?.trim(),
          numRegistreCommerce: saisie.numRegistreCommerce?.trim(),
        });

  const roles =
    saisie.roles !== undefined && saisie.roles.length > 0 ? { roles: [...saisie.roles] } : {};

  return appelerApi<ProfessionnelListe>('/professionnels', {
    methode: 'POST',
    jeton,
    corps: { ...commun, ...propreALaCategorie, ...roles },
  });
}

/**
 * Modifie un professionnel et, le cas échéant, change son statut (US-013).
 *
 * `categorie`, `numRegistreCommerce`, `email`, la pièce d'identité et la
 * naissance sont **absents du contrat de l'API** : une personne physique ne
 * devient pas une personne morale, et les identifiants uniques ne se corrigent
 * pas au guichet. Les proposer donnerait un formulaire qui perd silencieusement
 * la moitié de sa saisie.
 */
export async function modifierProfessionnel(
  jeton: string,
  idProfessionnel: number,
  modifications: {
    readonly nom?: string | undefined;
    readonly prenom?: string | undefined;
    readonly raisonSociale?: string | undefined;
    readonly nationalite?: string | undefined;
    readonly telephone?: string | undefined;
    readonly adresse?: string | undefined;
    readonly statut?: StatutProfessionnel | undefined;
  },
): Promise<ProfessionnelListe> {
  return appelerApi<ProfessionnelListe>(`/professionnels/${String(idProfessionnel)}`, {
    methode: 'PATCH',
    jeton,
    corps: sansVides({ ...modifications }),
  });
}

/** Nom d'affichage d'un acteur, quelle que soit sa catégorie. */
export function nommerProfessionnel(pro: {
  readonly nom: string | null;
  readonly prenom: string | null;
  readonly raisonSociale: string | null;
}): string {
  if (pro.raisonSociale !== null && pro.raisonSociale !== '') return pro.raisonSociale;
  const nom = [pro.prenom, pro.nom].filter((part) => part !== null && part !== '').join(' ');
  return nom === '' ? 'Sans nom' : nom;
}

// -----------------------------------------------------------------------------
// Engins de pêche et armement des embarcations (US-064, US-069)
// -----------------------------------------------------------------------------

export interface TypeEngin {
  readonly idTypeEngin: number;
  readonly code: string;
  readonly libelle: string;
}

/**
 * Fiche d'engin au catalogue.
 *
 * `maillageMm` et `longueurM` sont des `DECIMAL` en base : l'API les rend en
 * **chaîne**, non en nombre. Les déclarer `number` ici mentirait sur le contrat
 * et ferait échouer silencieusement toute arithmétique côté client.
 */
export interface EnginListe {
  readonly idEngin: number;
  readonly libelle: string;
  readonly idTypeEngin: number;
  readonly maillageMm: string | null;
  readonly longueurM: string | null;
  readonly autorise: boolean;
  readonly observations: string | null;
  readonly typeEngin: { readonly code: string; readonly libelle: string };
}

export interface SaisieEngin {
  readonly libelle: string;
  readonly idTypeEngin: number;
  readonly maillageMm?: number | undefined;
  readonly longueurM?: number | undefined;
  readonly autorise?: boolean | undefined;
  readonly observations?: string | undefined;
}

export interface FiltresEngins {
  readonly recherche?: string;
  readonly idTypeEngin?: number | '';
  readonly autorise?: boolean | undefined;
  readonly page?: number;
}

export async function listerTypesEngin(
  jeton: string,
  signal?: AbortSignal,
): Promise<readonly TypeEngin[]> {
  return appelerApi<readonly TypeEngin[]>('/engins/types', {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export async function listerEngins(
  jeton: string,
  filtres: FiltresEngins = {},
  signal?: AbortSignal,
): Promise<Page<EnginListe>> {
  const parametres = new URLSearchParams();
  if (filtres.recherche !== undefined && filtres.recherche.trim() !== '') {
    parametres.set('recherche', filtres.recherche.trim());
  }
  if (filtres.idTypeEngin !== undefined && filtres.idTypeEngin !== '') {
    parametres.set('idTypeEngin', String(filtres.idTypeEngin));
  }
  if (filtres.autorise !== undefined) {
    parametres.set('autorise', String(filtres.autorise));
  }
  parametres.set('page', String(filtres.page ?? 1));
  return appelerApi<Page<EnginListe>>(`/engins?${parametres.toString()}`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Inscrit un engin au catalogue (US-064).
 *
 * `autorise` traverse `sansVides` sans dommage — ce filtre n'écarte que
 * `undefined`, `null` et la chaîne vide, jamais `false`. C'est ce qui permet de
 * déclarer un engin **prohibé** dès sa création : le serveur refusera ensuite
 * toute installation sur une coque.
 */
export async function creerEngin(jeton: string, saisie: SaisieEngin): Promise<EnginListe> {
  return appelerApi<EnginListe>('/engins', {
    methode: 'POST',
    jeton,
    corps: sansVides({
      libelle: saisie.libelle.trim(),
      idTypeEngin: saisie.idTypeEngin,
      maillageMm: saisie.maillageMm,
      longueurM: saisie.longueurM,
      autorise: saisie.autorise,
      observations: saisie.observations?.trim(),
    }),
  });
}

export async function modifierEngin(
  jeton: string,
  idEngin: number,
  modifications: Partial<SaisieEngin>,
): Promise<EnginListe> {
  return appelerApi<EnginListe>(`/engins/${String(idEngin)}`, {
    methode: 'PATCH',
    jeton,
    corps: sansVides({ ...modifications }),
  });
}

/** Installation d'un engin sur une coque, telle que la rend la fiche embarcation. */
export interface InstallationEngin {
  readonly idEmbarcation: number;
  readonly idEngin: number;
  readonly nombre: number | null;
  readonly dateInstallation: string;
  readonly dateRetrait: string | null;
  readonly engin: EnginListe;
}

/**
 * Arme une embarcation d'un engin (US-069).
 *
 * Le serveur refuse un engin prohibé et une seconde installation active du même
 * engin sur la même coque. L'interface ne double pas ces contrôles : elle en
 * rend le message.
 */
export async function armerEmbarcation(
  jeton: string,
  idEmbarcation: number,
  saisie: {
    readonly idEngin: number;
    readonly nombre?: number;
    readonly dateInstallation?: string;
  },
): Promise<InstallationEngin> {
  return appelerApi<InstallationEngin>(`/embarcations/${String(idEmbarcation)}/engins`, {
    methode: 'POST',
    jeton,
    corps: sansVides({ ...saisie }),
  });
}

/**
 * Retire un engin d'une embarcation : **clôt** l'installation active, ne l'efface
 * pas. C'est l'historique daté qui permet de savoir avec quoi une coque pêchait à
 * une date donnée — un contrôle en mer se juge à cette date, pas à aujourd'hui.
 */
export async function desarmerEmbarcation(
  jeton: string,
  idEmbarcation: number,
  idEngin: number,
  dateRetrait?: string,
): Promise<InstallationEngin> {
  return appelerApi<InstallationEngin>(
    `/embarcations/${String(idEmbarcation)}/engins/${String(idEngin)}/retrait`,
    {
      methode: 'POST',
      jeton,
      corps: sansVides({ dateRetrait }),
    },
  );
}

// -----------------------------------------------------------------------------
// Fiche d'une embarcation (US-014, US-064)
// -----------------------------------------------------------------------------

export interface EmbarcationFiche extends EmbarcationListe {
  readonly idProprietaire: number;
  readonly idArmateur: number | null;
  readonly longueurHorsTout: string | null;
  readonly materiau: string | null;
  readonly motorisee: boolean;
  readonly zonePecheHabituelle: string | null;
  readonly dateEnregistrement: string;
  readonly typeAcquisition: string | null;
  readonly pavillon: string | null;
  readonly dateFabrication: string | null;
  readonly lieuFabrication: string | null;
  readonly dateNationalite: string | null;
  readonly dateExpNationalite: string | null;
  readonly typeNavire: string | null;
  readonly jaugeBrute: string | null;
  readonly proprietaire: {
    readonly idProfessionnel: number;
    readonly categorie: string;
    readonly nom: string | null;
    readonly prenom: string | null;
    readonly raisonSociale: string | null;
  };
  readonly armateur: {
    readonly idProfessionnel: number;
    readonly categorie: string;
    readonly nom: string | null;
    readonly prenom: string | null;
    readonly raisonSociale: string | null;
  } | null;
  readonly embarcationEngins: readonly InstallationEngin[];
}

export async function obtenirEmbarcation(
  jeton: string,
  idEmbarcation: number,
  signal?: AbortSignal,
): Promise<EmbarcationFiche> {
  return appelerApi<EmbarcationFiche>(`/embarcations/${String(idEmbarcation)}`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

// -----------------------------------------------------------------------------
// Administration des comptes (US-008, US-010)
// -----------------------------------------------------------------------------

export interface CompteListe {
  readonly idUtilisateur: number;
  readonly login: string;
  readonly email: string;
  readonly statutCompte: string;
  readonly nbEchecsConnexion: number;
  readonly forceChangementMdp: boolean;
  readonly typeActeur: string | null;
  readonly idAdministration: number | null;
  readonly sigleAdministration: string | null;
  readonly idProfessionnel: number | null;
  readonly roles: readonly string[];
}

/** Le mot de passe provisoire n'est rendu qu'ici, et une seule fois. */
export interface CompteAvecProvisoire extends CompteListe {
  readonly motDePasseProvisoire: string;
}

export interface FiltresComptes {
  readonly recherche?: string;
  readonly statut?: string;
  readonly role?: string;
  readonly page?: number;
  readonly taille?: number;
}

export async function listerComptes(
  jeton: string,
  filtres: FiltresComptes = {},
  signal?: AbortSignal,
): Promise<Page<CompteListe>> {
  const parametres = new URLSearchParams();
  if (filtres.recherche !== undefined && filtres.recherche !== '') {
    parametres.set('recherche', filtres.recherche);
  }
  if (filtres.statut !== undefined) parametres.set('statut', filtres.statut);
  if (filtres.role !== undefined) parametres.set('role', filtres.role);
  if (filtres.page !== undefined) parametres.set('page', String(filtres.page));

  const requete = parametres.toString();
  return appelerApi<Page<CompteListe>>(`/utilisateurs${requete === '' ? '' : `?${requete}`}`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export async function creerCompte(
  jeton: string,
  compte: {
    login: string;
    email: string;
    roles: readonly string[];
    idAdministration?: number;
    idProfessionnel?: number;
  },
): Promise<CompteAvecProvisoire> {
  return appelerApi<CompteAvecProvisoire>('/utilisateurs', {
    methode: 'POST',
    jeton,
    corps: {
      login: compte.login,
      email: compte.email,
      roles: compte.roles,
      ...(compte.idAdministration === undefined
        ? {}
        : { idAdministration: compte.idAdministration }),
      ...(compte.idProfessionnel === undefined ? {} : { idProfessionnel: compte.idProfessionnel }),
    },
  });
}

export async function changerStatutCompte(
  jeton: string,
  idUtilisateur: number,
  statutCompte: 'actif' | 'desactive',
): Promise<CompteListe> {
  return appelerApi<CompteListe>(`/utilisateurs/${String(idUtilisateur)}`, {
    methode: 'PATCH',
    jeton,
    corps: { statutCompte },
  });
}

export async function deverrouillerCompte(
  jeton: string,
  idUtilisateur: number,
): Promise<CompteListe> {
  return appelerApi<CompteListe>(`/utilisateurs/${String(idUtilisateur)}/deverrouillage`, {
    methode: 'POST',
    jeton,
  });
}

export async function reinitialiserCompte(
  jeton: string,
  idUtilisateur: number,
): Promise<CompteAvecProvisoire> {
  return appelerApi<CompteAvecProvisoire>(
    `/utilisateurs/${String(idUtilisateur)}/reinitialisation`,
    {
      methode: 'POST',
      jeton,
    },
  );
}

// -----------------------------------------------------------------------------
// Rôles et permissions (US-010)
// -----------------------------------------------------------------------------

/**
 * Permission élémentaire : un **module** et une **action**.
 *
 * Le couple est unique en base (`@@unique([module, action])`), et c'est lui qui
 * se lit à l'écran — il n'y a pas de libellé rédigé à afficher à la place.
 */
export interface PermissionApi {
  readonly idPermission: number;
  readonly module: string;
  readonly action: string;
}

export interface RoleAvecPermissions {
  readonly idRole: number;
  readonly libelleRole: string;
  /** 1 à 5. Le niveau d'accès **n'est pas** administrable depuis l'interface. */
  readonly niveauAcces: number;
  readonly description: string | null;
  readonly permissions: readonly PermissionApi[];
}

export async function listerRoles(
  jeton: string,
  signal?: AbortSignal,
): Promise<readonly RoleAvecPermissions[]> {
  return appelerApi<readonly RoleAvecPermissions[]>('/roles', {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export async function listerPermissions(
  jeton: string,
  signal?: AbortSignal,
): Promise<readonly PermissionApi[]> {
  return appelerApi<readonly PermissionApi[]>('/roles/permissions', {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Remplace **en bloc** les permissions d'un rôle (US-010).
 *
 * Le verbe est un `PUT` et non un `PATCH` : le serveur efface puis réécrit
 * l'ensemble dans une transaction. L'interface envoie donc toujours la liste
 * complète, jamais un delta — transmettre les seules cases cochées depuis le
 * dernier envoi retirerait silencieusement tout le reste.
 *
 * Le serveur refuse le rôle `admin_systeme` : ce sont ses permissions qui
 * donnent accès à cet écran, et les retirer le fermerait sans retour possible
 * hors SQL.
 */
export async function definirPermissionsRole(
  jeton: string,
  idRole: number,
  idPermissions: readonly number[],
): Promise<{ readonly idRole: number }> {
  return appelerApi(`/roles/${String(idRole)}/permissions`, {
    methode: 'PUT',
    jeton,
    corps: { idPermissions: [...idPermissions] },
  });
}

export interface AdministrationListe {
  readonly idAdministration: number;
  readonly sigle: string;
  /** Nom complet tel que la table `administration` le porte (`nom_complet`). */
  readonly nomComplet: string;
}

/** Référentiel des administrations, pour rattacher un compte d'agent. */
export async function listerAdministrations(
  jeton: string,
  signal?: AbortSignal,
): Promise<readonly AdministrationListe[]> {
  return appelerApi<readonly AdministrationListe[]>('/utilisateurs/administrations', {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

// -----------------------------------------------------------------------------
// Référentiel des types d'autorisation (US-066)
// -----------------------------------------------------------------------------

export interface TypeAutorisationAdmin extends TypeAutorisation {
  readonly idTypeParent: number | null;
  readonly delaiSlaJours: number;
  readonly paiementRequis: boolean;
  readonly actif: boolean;
  /** Nombre de dossiers déposés sous ce type : dit ce qu'on risque à le retirer. */
  readonly nbDemandes: number;
  readonly nbTarifs: number;
}

export async function listerTypesReferentiel(
  jeton: string,
  portee: 'tous' | 'actifs' | 'retires',
  signal?: AbortSignal,
): Promise<readonly TypeAutorisationAdmin[]> {
  return appelerApi<readonly TypeAutorisationAdmin[]>(
    `/referentiel/types-autorisation?portee=${portee}`,
    { jeton, ...(signal === undefined ? {} : { signal }) },
  );
}

export interface SaisieTypeAutorisation {
  readonly code?: string;
  readonly libelle?: string;
  readonly embarcationRequise?: boolean;
  readonly dureeValiditeMois?: number;
  readonly delaiSlaJours?: number;
  readonly paiementRequis?: boolean;
  readonly actif?: boolean;
}

export async function creerTypeReferentiel(
  jeton: string,
  saisie: SaisieTypeAutorisation,
): Promise<TypeAutorisationAdmin> {
  return appelerApi<TypeAutorisationAdmin>('/referentiel/types-autorisation', {
    methode: 'POST',
    jeton,
    corps: saisie,
  });
}

export async function modifierTypeReferentiel(
  jeton: string,
  idTypeAutorisation: number,
  saisie: SaisieTypeAutorisation,
): Promise<TypeAutorisationAdmin> {
  return appelerApi<TypeAutorisationAdmin>(
    `/referentiel/types-autorisation/${String(idTypeAutorisation)}`,
    { methode: 'PATCH', jeton, corps: saisie },
  );
}

// -----------------------------------------------------------------------------
// Import CSV des pirogues (US-015)
// -----------------------------------------------------------------------------

export interface AnomalieImport {
  readonly ligne: number;
  readonly colonne: string | null;
  readonly motif: string;
}

export interface RapportImport {
  readonly lues: number;
  readonly creees: number;
  readonly rejetees: number;
  readonly separateur: string;
  readonly simulation: boolean;
  readonly anomalies: readonly AnomalieImport[];
  readonly immatriculations: readonly string[];
}

/**
 * Envoie le fichier **en texte brut**, comme il sort du tableur.
 *
 * `simulation` lit, résout et contrôle tout sans rien écrire : c'est le mode
 * par lequel une direction régionale éprouve son fichier avant de l'engager.
 */
export async function importerPirogues(
  jeton: string,
  contenu: string,
  simulation: boolean,
): Promise<RapportImport> {
  return appelerApi<RapportImport>(`/embarcations/import${simulation ? '?simulation=true' : ''}`, {
    methode: 'POST',
    jeton,
    corpsTexte: { contenu, typeMime: 'text/csv; charset=utf-8' },
  });
}

// -----------------------------------------------------------------------------
// Équipage (US-016)
// -----------------------------------------------------------------------------

export interface MarinResume {
  readonly idProfessionnel: number;
  readonly categorie: string;
  readonly nom: string | null;
  readonly prenom: string | null;
  readonly raisonSociale: string | null;
  readonly nationalite: string;
}

export interface LigneEquipage {
  readonly idEmbarcation: number;
  readonly idProfessionnel: number;
  readonly roleABord: string;
  readonly dateEmbarquement: string;
  readonly dateDebarquement: string | null;
  readonly numAutorisationEmb: string | null;
  readonly professionnel: MarinResume;
}

export async function listerEquipage(
  jeton: string,
  idEmbarcation: number,
  historique: boolean,
  signal?: AbortSignal,
): Promise<readonly LigneEquipage[]> {
  return appelerApi<readonly LigneEquipage[]>(
    `/embarcations/${String(idEmbarcation)}/equipage${historique ? '?historique=true' : ''}`,
    { jeton, ...(signal === undefined ? {} : { signal }) },
  );
}

export async function embarquerMarin(
  jeton: string,
  idEmbarcation: number,
  saisie: {
    readonly idProfessionnel: number;
    readonly roleABord: string;
    readonly dateEmbarquement: string;
    readonly numAutorisationEmb?: string;
  },
): Promise<LigneEquipage> {
  return appelerApi<LigneEquipage>(`/embarcations/${String(idEmbarcation)}/equipage`, {
    methode: 'POST',
    jeton,
    corps: saisie,
  });
}

export async function debarquerMarin(
  jeton: string,
  idEmbarcation: number,
  saisie: {
    readonly idProfessionnel: number;
    readonly dateEmbarquement: string;
    readonly dateDebarquement: string;
  },
): Promise<LigneEquipage> {
  return appelerApi<LigneEquipage>(`/embarcations/${String(idEmbarcation)}/equipage/debarquement`, {
    methode: 'POST',
    jeton,
    corps: saisie,
  });
}

// -----------------------------------------------------------------------------
// Renouvellement (US-024)
// -----------------------------------------------------------------------------

export type NiveauAlerteTitre = 'aucun' | 'proche' | 'imminente' | 'expire';

export interface DecisionRenouvellementApi {
  readonly autorise: boolean;
  readonly blocages: readonly string[];
  readonly niveauAlerte: NiveauAlerteTitre;
  readonly joursAvantExpiration: number;
  readonly finDelaiGrace: string;
}

export interface EcheanceTitre {
  readonly type: 'licence' | 'permis';
  readonly id: number;
  readonly numeroOfficiel: string | null;
  readonly immatriculation: string;
  readonly dateExpiration: string;
  readonly decision: DecisionRenouvellementApi;
}

/** Titres du périmètre dont l'échéance approche, du plus urgent au moins. */
export async function listerEcheances(
  jeton: string,
  signal?: AbortSignal,
): Promise<readonly EcheanceTitre[]> {
  return appelerApi<readonly EcheanceTitre[]>('/titres/echeances', {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export async function evaluerRenouvellement(
  jeton: string,
  type: 'licence' | 'permis',
  id: number,
  signal?: AbortSignal,
): Promise<DecisionRenouvellementApi> {
  return appelerApi<DecisionRenouvellementApi>(`/titres/${type}/${String(id)}/renouvellement`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export interface DossierRenouvellement {
  readonly idDemande: number;
  readonly referenceDemande: string;
  readonly statut: string;
}

export async function deposerRenouvellement(
  jeton: string,
  type: 'licence' | 'permis',
  id: number,
): Promise<DossierRenouvellement> {
  return appelerApi<DossierRenouvellement>(`/titres/${type}/${String(id)}/renouvellement`, {
    methode: 'POST',
    jeton,
  });
}

/**
 * Traduction des motifs de refus rendus par le moteur.
 *
 * Le serveur rend des codes stables ; c'est ici qu'ils deviennent des phrases.
 * Un code inconnu est rendu tel quel plutôt que masqué : mieux vaut un libellé
 * technique qu'un blocage silencieux dont l'usager ne saurait rien.
 */
export function libelleBlocageRenouvellement(code: string): string {
  const table: Record<string, string> = {
    RENOUVELLEMENT_TROP_TOT:
      'Le renouvellement s’ouvre 60 jours avant l’échéance. Revenez à ce moment-là.',
    RENOUVELLEMENT_TITRE_ANNULE: 'Ce titre a été annulé : il ne se renouvelle pas.',
    RENOUVELLEMENT_TITRE_SUSPENDU:
      'Ce titre est suspendu. Faites d’abord lever la suspension auprès de votre administration.',
    RENOUVELLEMENT_DELAI_GRACE_DEPASSE:
      'L’échéance est dépassée depuis plus de 30 jours : déposez une nouvelle demande.',
    RENOUVELLEMENT_DEJA_ENGAGE: 'Un renouvellement de ce titre est déjà en cours.',
  };
  return table[code] ?? code;
}

// -----------------------------------------------------------------------------
// Notifications (US-033, US-049, US-050)
// -----------------------------------------------------------------------------

export interface NotificationListe {
  readonly idNotification: number;
  readonly typeNotif: string;
  readonly message: string;
  readonly lu: boolean;
  readonly horodatage: string;
  readonly lienAction: string | null;
}

export interface BoiteNotifications {
  readonly donnees: readonly NotificationListe[];
  readonly nonLues: number;
}

/**
 * Ma boîte. Aucun identifiant d'usager n'entre dans l'appel : le serveur la
 * déduit du jeton, si bien qu'il n'existe aucun paramètre par lequel demander
 * celle d'un autre.
 */
export async function listerNotifications(
  jeton: string,
  options: { readonly nonLues?: boolean; readonly taille?: number } = {},
  signal?: AbortSignal,
): Promise<BoiteNotifications> {
  const parametres = new URLSearchParams();
  if (options.nonLues === true) parametres.set('nonLues', 'true');
  if (options.taille !== undefined) parametres.set('taille', String(options.taille));
  const suffixe = parametres.toString();

  return appelerApi<BoiteNotifications>(`/notifications${suffixe === '' ? '' : `?${suffixe}`}`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export async function marquerNotificationLue(
  jeton: string,
  idNotification: number,
): Promise<{ readonly idNotification: number; readonly lu: true }> {
  return appelerApi(`/notifications/${String(idNotification)}/lecture`, {
    methode: 'PATCH',
    jeton,
  });
}

export async function marquerToutesNotificationsLues(
  jeton: string,
): Promise<{ readonly marquees: number }> {
  return appelerApi('/notifications/lecture', { methode: 'POST', jeton });
}

export interface PreferenceCanal {
  readonly idCanal: number;
  readonly code: string;
  readonly libelle: string;
  readonly actif: boolean;
  readonly administrable: boolean;
  readonly exigeNumero: boolean;
  readonly optIn: boolean;
  readonly dateConsentement: string | null;
  /** Numéro **masqué** par le serveur : le client ne le reçoit jamais entier. */
  readonly numeroTelephone: string | null;
  readonly numeroVerifie: boolean;
  readonly verificationEnCours: boolean;
}

export async function listerPreferencesNotification(
  jeton: string,
  signal?: AbortSignal,
): Promise<readonly PreferenceCanal[]> {
  return appelerApi<readonly PreferenceCanal[]>('/notifications/preferences', {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export interface PreferenceEnregistree {
  readonly idCanal: number;
  readonly code: string;
  readonly optIn: boolean;
  readonly numeroTelephone: string | null;
  readonly numeroVerifie: boolean;
  readonly verificationRequise: boolean;
}

export async function definirPreferenceNotification(
  jeton: string,
  idCanal: number,
  valeur: { readonly optIn: boolean; readonly numeroTelephone?: string },
): Promise<PreferenceEnregistree> {
  return appelerApi<PreferenceEnregistree>(`/notifications/preferences/${String(idCanal)}`, {
    methode: 'PUT',
    jeton,
    corps: valeur,
  });
}

export async function envoyerCodeVerification(
  jeton: string,
  idCanal: number,
): Promise<{ readonly envoye: true; readonly expiration: string; readonly canal: string }> {
  return appelerApi(`/notifications/preferences/${String(idCanal)}/code`, {
    methode: 'POST',
    jeton,
  });
}

export async function verifierNumeroNotification(
  jeton: string,
  idCanal: number,
  code: string,
): Promise<{ readonly numeroVerifie: true; readonly dateVerification: string }> {
  return appelerApi(`/notifications/preferences/${String(idCanal)}/verification`, {
    methode: 'POST',
    jeton,
    corps: { code },
  });
}

// -----------------------------------------------------------------------------
// Paiement des redevances (EP-12, US-041 à US-047)
// -----------------------------------------------------------------------------

export type StatutAvisApi = 'emis' | 'partiel' | 'paye' | 'annule' | 'expire';

export interface AvisPaiementListe {
  readonly idAvis: number;
  readonly numeroAvis: string;
  readonly idDemande: number;
  readonly referenceDemande: string;
  readonly idEmbarcation: number | null;
  readonly immatriculation: string | null;
  readonly montantDu: number;
  readonly montantPaye: number;
  readonly restantDu: number;
  readonly devise: string;
  readonly statut: StatutAvisApi;
  readonly dateEmission: string;
  readonly dateEcheance: string;
  readonly observations: string | null;
}

/**
 * Avis visibles par l'appelant.
 *
 * Aucun paramètre ne désigne un titulaire : le **serveur seul** cloisonne — un
 * professionnel ne reçoit que les avis de ses dossiers, un agent voit la
 * caisse. Un filtre côté client ne serait pas un contrôle d'accès.
 */
export async function listerAvisPaiement(
  jeton: string,
  filtres: { readonly statut?: StatutAvisApi; readonly idDemande?: number } = {},
  signal?: AbortSignal,
): Promise<readonly AvisPaiementListe[]> {
  const parametres = new URLSearchParams();
  if (filtres.statut !== undefined) parametres.set('statut', filtres.statut);
  if (filtres.idDemande !== undefined) parametres.set('idDemande', String(filtres.idDemande));
  const requete = parametres.toString();

  return appelerApi<readonly AvisPaiementListe[]>(
    `/paiement/avis${requete === '' ? '' : `?${requete}`}`,
    { jeton, ...(signal === undefined ? {} : { signal }) },
  );
}

export async function consulterAvisPaiement(
  jeton: string,
  idAvis: number,
  signal?: AbortSignal,
): Promise<AvisPaiementListe> {
  return appelerApi<AvisPaiementListe>(`/paiement/avis/${String(idAvis)}`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export interface CompteRenduEmissionApi {
  readonly idDemande: number;
  readonly avis: readonly { idAvis: number; numeroAvis: string; montantDu: number }[];
  readonly montantTotal: number;
  readonly devise: string;
}

/** Émet les avis d'un dossier. Aucun montant n'est envoyé : le barème fait foi. */
export async function emettreAvisPaiement(
  jeton: string,
  idDemande: number,
  dateEcheance?: string,
): Promise<CompteRenduEmissionApi> {
  return appelerApi<CompteRenduEmissionApi>(`/paiement/demandes/${String(idDemande)}/avis`, {
    methode: 'POST',
    jeton,
    corps: dateEcheance === undefined ? {} : { dateEcheance },
  });
}

export type CanalPaiementApi = 'mobile_money' | 'virement_bancaire' | 'especes_tresor' | 'cheque';

export interface ReglementOuvert {
  readonly idTransaction: number;
  readonly statut: string;
  readonly montant: number;
  readonly devise: string;
  readonly referenceExterne: string | null;
  /** Instruction à afficher à l'usager, rendue par la passerelle le cas échéant. */
  readonly consigne?: string | null;
}

export interface SaisieReglement {
  readonly canal: CanalPaiementApi;
  readonly montant: number;
  readonly devise?: string;
  readonly compte?: string;
  readonly operateur?: string;
  readonly referenceExterne?: string;
}

/** Ouvre un règlement. Rien n'est imputé ici : la confirmation seule encaisse. */
export async function ouvrirReglement(
  jeton: string,
  idAvis: number,
  saisie: SaisieReglement,
): Promise<ReglementOuvert> {
  return appelerApi<ReglementOuvert>(`/paiement/avis/${String(idAvis)}/reglements`, {
    methode: 'POST',
    jeton,
    corps: saisie,
  });
}

export interface ReglementImpute {
  readonly idTransaction: number;
  readonly statut: string;
  readonly avis: { readonly idAvis: number; readonly statut: StatutAvisApi };
  readonly restantDu: number;
  readonly solde: boolean;
}

/** Confirme un règlement de guichet, pièce en main. Agents de caisse seuls. */
export async function confirmerReglement(
  jeton: string,
  idTransaction: number,
  pieces: { readonly referenceExterne?: string; readonly piece?: string } = {},
): Promise<ReglementImpute> {
  return appelerApi<ReglementImpute>(`/paiement/reglements/${String(idTransaction)}/confirmation`, {
    methode: 'POST',
    jeton,
    corps: pieces,
  });
}

/** Annule un avis émis à tort. Refusé dès qu'un règlement y est imputé. */
/**
 * Clôt un règlement qui n'aboutira pas (US-043).
 *
 * Le guichet ouvre puis confirme en un seul geste. Si la **confirmation** échoue
 * — coupure réseau, montant refusé — la transaction reste `en_attente` et
 * personne ne peut plus la solder : elle fausserait le rapprochement de caisse.
 * Cet appel la referme, avec son motif consigné à l'audit.
 *
 * Le serveur exige un motif d'au moins cinq caractères : un échec sans raison
 * n'est pas exploitable par le contrôleur de caisse.
 */
export async function echouerReglement(
  jeton: string,
  idTransaction: number,
  motif: string,
): Promise<unknown> {
  return appelerApi(`/paiement/reglements/${String(idTransaction)}/echec`, {
    methode: 'POST',
    jeton,
    corps: { motif },
  });
}

export async function annulerAvisPaiement(
  jeton: string,
  idAvis: number,
  motif: string,
): Promise<{ readonly idAvis: number; readonly statut: StatutAvisApi }> {
  return appelerApi(`/paiement/avis/${String(idAvis)}/annulation`, {
    methode: 'POST',
    jeton,
    corps: { motif },
  });
}

export interface ConstatRapprochementApi {
  readonly ecart: string;
  readonly message: string;
  readonly detail: Readonly<Record<string, unknown>>;
}

export interface RapprochementApi {
  readonly conforme: boolean;
  readonly montantPayeReel: number;
  readonly statutAttendu: StatutAvisApi;
  readonly restantDu: number;
  readonly constats: readonly ConstatRapprochementApi[];
}

/** Rapproche un avis de son grand livre. Constate, ne corrige pas. */
export async function rapprocherAvisPaiement(
  jeton: string,
  idAvis: number,
  signal?: AbortSignal,
): Promise<RapprochementApi> {
  return appelerApi<RapprochementApi>(`/paiement/avis/${String(idAvis)}/rapprochement`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

/** Quittance d'un avis acquitté. Refusée en 409 tant qu'il reste un solde. */
export async function telechargerQuittance(jeton: string, idAvis: number): Promise<FichierApi> {
  return telechargerApi(`/paiement/avis/${String(idAvis)}/quittance`, { jeton });
}

// -----------------------------------------------------------------------------
// Journaux de pêche (EP-07, US-027 à US-029)
// -----------------------------------------------------------------------------

export type StatutJournalApi = 'ouvert' | 'cloture' | 'valide';

export interface CaptureApi {
  readonly espece: string;
  readonly quantiteKg: number;
}

export interface JournalPecheListe {
  readonly idJournal: number;
  readonly referenceJournal: string;
  readonly idEmbarcation: number;
  readonly immatriculation: string;
  readonly nomEmbarcation: string | null;
  readonly categorieEmbarcation: string;
  readonly idCapitaine: number;
  readonly capitaine: string;
  readonly dateDebutMaree: string;
  readonly dateFinMaree: string | null;
  readonly zonePecheReelle: string;
  readonly quantiteTotaleKg: number;
  readonly captures: readonly CaptureApi[];
  readonly statut: StatutJournalApi;
}

export interface PageJournaux {
  readonly donnees: readonly JournalPecheListe[];
  readonly total: number;
  readonly page: number;
  readonly taille: number;
}

/** Marées visibles par l'appelant. Le serveur seul cloisonne. */
export async function listerJournaux(
  jeton: string,
  filtres: { readonly statut?: StatutJournalApi; readonly idEmbarcation?: number } = {},
  signal?: AbortSignal,
): Promise<PageJournaux> {
  const parametres = new URLSearchParams();
  if (filtres.statut !== undefined) parametres.set('statut', filtres.statut);
  if (filtres.idEmbarcation !== undefined) {
    parametres.set('idEmbarcation', String(filtres.idEmbarcation));
  }
  const requete = parametres.toString();
  return appelerApi<PageJournaux>(`/journaux${requete === '' ? '' : `?${requete}`}`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

/** Ouvre une marée. Une coque ne peut pas être en mer deux fois à la fois. */
export async function ouvrirJournal(
  jeton: string,
  saisie: {
    readonly idEmbarcation: number;
    readonly dateDebutMaree: string;
    readonly zonePecheReelle: string;
    readonly idCapitaine?: number;
  },
): Promise<JournalPecheListe> {
  return appelerApi<JournalPecheListe>('/journaux', { methode: 'POST', jeton, corps: saisie });
}

/**
 * Clôt une marée et déclare ses captures.
 *
 * **Aucun total n'est envoyé** : il est calculé par le serveur depuis le détail
 * par espèce, et c'est ce détail qui fait foi.
 */
export async function cloturerJournal(
  jeton: string,
  idJournal: number,
  saisie: {
    readonly dateFinMaree: string;
    readonly zonePecheReelle?: string;
    readonly captures?: readonly CaptureApi[];
  },
): Promise<JournalPecheListe> {
  return appelerApi<JournalPecheListe>(`/journaux/${String(idJournal)}/cloture`, {
    methode: 'POST',
    jeton,
    corps: saisie,
  });
}

/** Valide un journal clôturé. Irréversible : il entre dans la statistique nationale. */
export async function validerJournal(
  jeton: string,
  idJournal: number,
  observations?: string,
): Promise<JournalPecheListe> {
  return appelerApi<JournalPecheListe>(`/journaux/${String(idJournal)}/validation`, {
    methode: 'POST',
    jeton,
    corps: observations === undefined ? {} : { observations },
  });
}

// -----------------------------------------------------------------------------
// Tableau de bord (EP-08, US-030 à US-032)
// -----------------------------------------------------------------------------

export interface DelaiAdministrationApi {
  readonly idAdministration: number;
  readonly sigle: string;
  readonly ordreDansCircuit: number;
  readonly delaiAccordeJours: number;
  readonly delaiMoyenJours: number | null;
  readonly instructionsTraitees: number;
  readonly instructionsEnCours: number;
  readonly horsDelai: number;
}

export interface TableauDeBordApi {
  readonly genereLe: string;
  readonly activite: {
    readonly total: number;
    readonly soumises: number;
    readonly enInstruction: number;
    readonly pretSignature: number;
    readonly signees: number;
    readonly rejetees: number;
    readonly closes: number;
    readonly tauxAcceptation: number | null;
  };
  readonly tendance: readonly {
    readonly mois: string;
    readonly depots: number;
    readonly signatures: number;
    readonly rejets: number;
  }[];
  readonly delais: {
    readonly parAdministration: readonly DelaiAdministrationApi[];
    readonly delaiMoyenTotalJours: number | null;
    readonly dossiersMesures: number;
    readonly dansLesDelaisReferentiel: number | null;
    readonly cibles: {
      readonly referentielJours: number;
      readonly cadrageJoursOuvrables: number;
      readonly arbitrageEnAttente: boolean;
      readonly note: string;
    };
  };
  readonly production: {
    readonly totalKg: number;
    readonly mareesDeclarees: number;
    readonly mareesEnCours: number;
    readonly enAttenteValidation: number;
    readonly parEspece: readonly { readonly espece: string; readonly quantiteKg: number }[];
    readonly parZone: readonly { readonly zone: string; readonly quantiteKg: number }[];
    readonly tendance: readonly {
      readonly mois: string;
      readonly quantiteKg: number;
      readonly marees: number;
    }[];
  };
  readonly recettes: {
    readonly encaisseTotal: number;
    readonly emisTotal: number;
    readonly resteARecouvrer: number;
    readonly tauxRecouvrement: number | null;
    readonly avisEmis: number;
    readonly avisPartiels: number;
    readonly avisPayes: number;
    readonly avisEchus: number;
    readonly devise: string;
  };
  readonly flotte: {
    readonly navires: number;
    readonly pirogues: number;
    readonly total: number;
    readonly professionnelsActifs: number;
    readonly licencesActives: number;
  };
}

/** Tableau de bord national, en un seul aller-retour. */
export async function chargerTableauDeBord(
  jeton: string,
  signal?: AbortSignal,
): Promise<TableauDeBordApi> {
  return appelerApi<TableauDeBordApi>('/statistiques/tableau-de-bord', {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

// -----------------------------------------------------------------------------
// Tickets d'incident (EP-15, US-059 à US-063)
// -----------------------------------------------------------------------------

export type StatutTicketApi =
  'nouveau' | 'en_cours' | 'en_attente_demandeur' | 'resolu' | 'ferme' | 'rejete';

export type PrioriteTicketApi = 'basse' | 'normale' | 'haute' | 'critique';

export interface CategorieTicketApi {
  readonly idCategorie: number;
  readonly code: string;
  readonly libelle: string;
  readonly delaiSlaHeures: number;
}

/**
 * Geste que le moteur du serveur ouvre à l'appelant sur ce ticket.
 *
 * L'interface n'en propose aucun autre. Ce n'est pas un contrôle d'accès — le
 * serveur revérifie tout — mais le moyen de ne pas afficher un bouton qui
 * rendrait un 409, faute corrigée sur les titres le 08/08.
 */
export interface GesteTicketApi {
  readonly statut: StatutTicketApi;
  readonly motifRequis: boolean;
}

export interface SlaTicketApi {
  readonly echeance: string;
  readonly respecte: boolean | null;
  readonly enRetard: boolean;
  readonly heuresRestantes: number;
  readonly aRisque: boolean;
  readonly delaiHeures: number;
}

export interface TicketListe {
  readonly idTicket: number;
  readonly numeroTicket: string;
  readonly objet: string;
  readonly statut: StatutTicketApi;
  readonly priorite: PrioriteTicketApi;
  readonly categorie: { readonly code: string; readonly libelle: string };
  readonly demandeur: string;
  readonly idDemandeur: number;
  readonly agentAssigne: string | null;
  readonly idAgentAssigne: number | null;
  readonly referenceDemande: string | null;
  readonly idDemande: number | null;
  readonly dateCreation: string;
  readonly datePriseEnCharge: string | null;
  readonly dateResolution: string | null;
  readonly dateFermeture: string | null;
  readonly satisfaction: number | null;
  readonly sla: SlaTicketApi;
  readonly gestesPossibles: readonly GesteTicketApi[];
}

export interface CommentaireTicketApi {
  readonly idCommentaire: number;
  readonly auteur: string;
  readonly estDemandeur: boolean;
  readonly contenu: string;
  readonly interne: boolean;
  readonly horodatage: string;
}

export interface TicketFiche extends TicketListe {
  readonly description: string;
  readonly commentaires: readonly CommentaireTicketApi[];
}

/**
 * Page de tickets.
 *
 * `tronquee` n'est pas décoratif : la file d'assistance est classée en mémoire
 * par le moteur, sous un plafond. Quand il mord, l'écran doit le dire plutôt
 * que de laisser lire « voici la file ».
 */
export interface PageTickets extends Page<TicketListe> {
  readonly tronquee: boolean;
}

export async function listerCategoriesTicket(
  jeton: string,
  signal?: AbortSignal,
): Promise<readonly CategorieTicketApi[]> {
  const page = await appelerApi<{ readonly donnees: readonly CategorieTicketApi[] }>(
    '/tickets/categories',
    { jeton, ...(signal === undefined ? {} : { signal }) },
  );
  return page.donnees;
}

/** Tickets visibles de l'appelant. Le serveur seul cloisonne. */
export async function listerTickets(
  jeton: string,
  filtres: {
    readonly statut?: StatutTicketApi;
    readonly priorite?: PrioriteTicketApi;
    readonly idCategorie?: number;
    readonly mesTickets?: boolean;
    readonly page?: number;
  } = {},
  signal?: AbortSignal,
): Promise<PageTickets> {
  const parametres = new URLSearchParams();
  if (filtres.statut !== undefined) parametres.set('statut', filtres.statut);
  if (filtres.priorite !== undefined) parametres.set('priorite', filtres.priorite);
  if (filtres.idCategorie !== undefined) parametres.set('idCategorie', String(filtres.idCategorie));
  if (filtres.mesTickets === true) parametres.set('mesTickets', 'true');
  parametres.set('page', String(filtres.page ?? 1));
  return appelerApi<PageTickets>(`/tickets?${parametres.toString()}`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

export async function consulterTicket(
  jeton: string,
  idTicket: number,
  signal?: AbortSignal,
): Promise<TicketFiche> {
  return appelerApi<TicketFiche>(`/tickets/${String(idTicket)}`, {
    jeton,
    ...(signal === undefined ? {} : { signal }),
  });
}

/** Signale un incident. Le demandeur est toujours l'appelant. */
export async function signalerIncident(
  jeton: string,
  saisie: {
    readonly idCategorie: number;
    readonly priorite?: PrioriteTicketApi;
    readonly objet: string;
    readonly description: string;
    readonly idDemande?: number;
  },
): Promise<TicketFiche> {
  return appelerApi<TicketFiche>('/tickets', {
    methode: 'POST',
    jeton,
    corps: sansVides({
      idCategorie: saisie.idCategorie,
      priorite: saisie.priorite,
      objet: saisie.objet.trim(),
      description: saisie.description.trim(),
      idDemande: saisie.idDemande,
    }),
  });
}

/**
 * Ajoute un message au fil.
 *
 * `interne` est envoyé tel quel, jamais forcé côté client : c'est le serveur
 * qui refuse la note interne à un demandeur, et il faut que le refus se voie.
 */
export async function commenterTicket(
  jeton: string,
  idTicket: number,
  contenu: string,
  interne = false,
): Promise<TicketFiche> {
  return appelerApi<TicketFiche>(`/tickets/${String(idTicket)}/commentaires`, {
    methode: 'POST',
    jeton,
    corps: { contenu: contenu.trim(), interne },
  });
}

export async function prendreEnChargeTicket(jeton: string, idTicket: number): Promise<TicketFiche> {
  return appelerApi<TicketFiche>(`/tickets/${String(idTicket)}/prise-en-charge`, {
    methode: 'POST',
    jeton,
    corps: {},
  });
}

/** Fait avancer le ticket. Le moteur du serveur reste seul juge du permis. */
export async function changerStatutTicket(
  jeton: string,
  idTicket: number,
  statut: StatutTicketApi,
  motif?: string,
): Promise<TicketFiche> {
  return appelerApi<TicketFiche>(`/tickets/${String(idTicket)}/statut`, {
    methode: 'POST',
    jeton,
    corps: sansVides({ statut, motif: motif?.trim() }),
  });
}

/** Appréciation du demandeur, qui clôt le ticket. */
export async function noterTicket(
  jeton: string,
  idTicket: number,
  satisfaction: number,
  commentaire?: string,
): Promise<TicketFiche> {
  return appelerApi<TicketFiche>(`/tickets/${String(idTicket)}/satisfaction`, {
    methode: 'POST',
    jeton,
    corps: sansVides({ satisfaction, commentaire: commentaire?.trim() }),
  });
}
